import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '../../../foundation/altzero_base/base/contextapi/UserContext';
import {
  NO_BILL_FILTERS, applyFilterUpdate, filtersFromParams, restoredParams, savedFiltersKey, sharedFilterQuery,
  type BillFilters,
} from '../lib/billFilters';

// The shared bill filters (lib/billFilters), kept in the URL query string so a
// filtered view can be shared, bookmarked and refreshed. The shared part is also
// saved per tab, and a bill list opened with an EMPTY query (the sidebar links
// carry none) picks it up — that is how the filters follow the adviser between
// Bills, Bulk Bill Run and Bulk Customer Report.

function readSaved(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSaved(key: string, query: string): void {
  try {
    if (query) window.sessionStorage.setItem(key, query);
    else window.sessionStorage.removeItem(key);
  } catch {
    // Storage blocked (private window, embeds): the URL still carries the filters.
  }
}

export type BillFiltersState = {
  filters: BillFilters;
  /** The shared filters as a query string (no `?`): for links between the lists, and as an effect key. */
  query: string;
  setFilters: (patch: Partial<BillFilters>) => void;
  clearFilters: () => void;
  /** One write for shared filters and screen-specific params together ('' removes a param). */
  update: (patch: Partial<BillFilters>, extras?: Record<string, string>) => void;
  /** A screen-specific param, '' when absent. */
  param: (name: string) => string;
};

export function useBillFilters(): BillFiltersState {
  const [params, setParams] = useSearchParams();
  const search = params.toString();
  const filters = useMemo(() => filtersFromParams(new URLSearchParams(search)), [search]);
  const query = useMemo(() => sharedFilterQuery(new URLSearchParams(search)), [search]);
  const restoreChecked = useRef(false);
  // Saved per signed-in user: another account in the same tab never sees these.
  const storageKey = savedFiltersKey(useUser().user?.id);

  useEffect(() => {
    if (!storageKey) return;
    // Once per mount: an empty query takes the filters saved by the last list.
    if (!restoreChecked.current) {
      restoreChecked.current = true;
      const restored = restoredParams(new URLSearchParams(search), readSaved(storageKey));
      if (restored) {
        setParams(restored, { replace: true });
        return;
      }
    }
    // An empty shared query never overwrites what is saved (a link carrying only
    // `?run=` must not wipe it); clearing the filters is the explicit `update`.
    if (query) writeSaved(storageKey, query);
  }, [search, query, storageKey, setParams]);

  // `replace`: typing in search must not fill the history with one entry per key.
  const update = useCallback((patch: Partial<BillFilters>, extras: Record<string, string> = {}) => {
    if (storageKey && !sharedFilterQuery(applyFilterUpdate(new URLSearchParams(search), patch, extras))) {
      writeSaved(storageKey, '');
    }
    setParams((prev) => applyFilterUpdate(prev, patch, extras), { replace: true });
  }, [setParams, search, storageKey]);

  const setFilters = useCallback((patch: Partial<BillFilters>) => update(patch), [update]);
  const clearFilters = useCallback(() => update(NO_BILL_FILTERS), [update]);
  const param = useCallback((name: string) => new URLSearchParams(search).get(name) ?? '', [search]);

  return { filters, query, setFilters, clearFilters, update, param };
}
